import React from 'react';
import dayjs from 'dayjs';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Card } from '../../components/Card';
import { CheckinEditorSummary } from './CheckinEditorSummary';
import { CardHeading } from '../components/CardHeading';
import { CardSubHeading } from '../components/CardSubHeading';
import { CheckinDetailsCard } from './CheckinDetailsCard';
import { CheckinNoteCard } from './CheckinNoteCard';
import { FeelingSelector } from './FeelingSelector';
import { CheckinState } from '../model/CheckinState';
import { INote } from '../../notes/generic-notes/INote';
import { ICheckinTag, IEmotion, IEnergy, IFeeling, ILifeEvent, IPersonalTime } from '../model/ICheckin';
import { EnergySelector } from './EnergySelector';
import { PersonalTimeSelector } from './PersonalTimeSelector';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { CreateAccountButton } from '../../auth/CreateAccountButton';
import { Text } from '../../components/Text';

export const PersonalCheckinEditor: React.FC = () => {

    const checkinState = useInstance(CheckinState);
    useSubscription(() => checkinState);

    const checkin = checkinState.getEditingCheckin('personal');

    const checkinDate = checkin ? checkinState.getCheckinDate(checkin) : undefined;
    const isToday = !checkinDate || dayjs(checkinDate).isToday();

    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    const onChangeFeeling = async (feeling?: IFeeling) => {
        if (feeling !== checkin!.feeling) {
            checkinState.savePersonalCheckin({ ...checkin!, feeling });
        }
    };

    const onChangeEmotions = (tags: ICheckinTag[]) => {
        checkinState.savePersonalCheckin({ ...checkin!, emotions: tags as IEmotion[] });
    };

    const onChangeLifeEvents = (tags: ICheckinTag[]) => {
        checkinState.savePersonalCheckin({ ...checkin!, lifeEvents: tags as ILifeEvent[] });
    };

    const onChangeEnergy = async (energy?: IEnergy) => {
        if (energy !== checkin!.energy) {
            checkinState.savePersonalCheckin({ ...checkin!, energy });
        }
    };

    const onChangePersonalTime = async (personalTime?: IPersonalTime) => {
        if (personalTime !== checkin!.personalTime) {
            checkinState.savePersonalCheckin({ ...checkin!, personalTime });
        }
    };

    const onChangeNote = (updatedNote: INote) => {
        checkinState.savePersonalCheckin({ ...checkin!, note: updatedNote.html, attachments: updatedNote.attachments, tags: updatedNote.tags });
    };

    const summaryText = 'Take a moment for yourself—reflect on your day, track your well-being, and gain insights into your caregiving balance.';

    if (isAnonymous) {
        return (
            <>
                <CheckinEditorSummary>{summaryText}</CheckinEditorSummary>

                <div style={{margin: '-12px 12px'}}>
                    <div style={{margin: '20px auto'}}><CreateAccountButton trackingFrom="Journal" /></div>
                    <Text>Please sign in to start journaling!</Text>
                </div>
            </>
        );
    }

    return (
        <>
            <CheckinEditorSummary>{summaryText}</CheckinEditorSummary>

            <Card padded>
                <CardHeading>How was your day?</CardHeading>
                <FeelingSelector value={checkin?.feeling} onChange={onChangeFeeling} />
            </Card>

            <CheckinDetailsCard
                type="EMOTIONS"
                tags={checkin?.emotions || []}
                onChange={onChangeEmotions}
            />

            <CheckinDetailsCard
                type="LIFE_EVENTS"
                tags={checkin?.lifeEvents || []}
                onChange={onChangeLifeEvents}
            />

            {/* can no longer add care events to personal checkins,
            but if they exist on old checkins, show them readonly */}
            {Boolean((checkin as any)?.careEvents?.length) &&
                <CheckinDetailsCard
                    type="CARE_EVENTS"
                    tags={(checkin as any).careEvents}
                    onChange={() => {}}
                    readonly
                />
            }

            <Card padded>
                <CardHeading>Energy</CardHeading>
                <CardSubHeading>Were you running on empty or fully charged{isToday ? ' today' : ''}?</CardSubHeading>
                <EnergySelector value={checkin?.energy} onChange={onChangeEnergy} />
            </Card>

            <Card padded>
                <CardHeading>Personal Time</CardHeading>
                <CardSubHeading>Did you spend any time for yourself{isToday ? ' today' : ''}?</CardSubHeading>
                <PersonalTimeSelector value={checkin?.personalTime} onChange={onChangePersonalTime} />
            </Card>

            <CheckinNoteCard
                checkin={checkin!}
                heading={isToday ? `Today's Note` : 'Note'}
                onChange={onChangeNote}
            />
        </>
    );
};
